import React, { useState, useEffect } from 'react';
import { apiClient } from '../utils/api';
import { WorkflowRegistry, RegisterWorkflowInput } from '../types/analytics';
import toast from 'react-hot-toast';
import { getAvailableWorkflowTags } from '../components/workflow-configs/WorkflowConfigRegistry';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Plus, Play, Pencil, Trash2, Loader2 } from 'lucide-react';

/**
 * AdminWorkflowRegistry Page
 *
 * Admin-only page for managing workflow registry (webhook-based workflows).
 * Features:
 * - List all registered workflows
 * - Create new workflow registration
 * - Update existing workflows (inline editing)
 * - Delete workflows
 * - Test workflow execution
 * - Input validation
 */
export const AdminWorkflowRegistry: React.FC = () => {
  const [workflows, setWorkflows] = useState<WorkflowRegistry[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [executingId, setExecutingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<RegisterWorkflowInput>({
    workflow_id_n8n: '',
    workflow_name: '',
    webhook_url: '',
    display_name: '',
    webhook_token: '',
    tags: [],
    description: '',
  });

  useEffect(() => {
    fetchWorkflows();
  }, []);

  const fetchWorkflows = async () => {
    try {
      setLoading(true);
      const data = await apiClient.getWorkflowRegistry();
      setWorkflows(data);
    } catch (error) {
      toast.error('Failed to load workflows');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (workflow: WorkflowRegistry) => {
    setEditingId(workflow.id);
    setFormData({
      workflow_id_n8n: workflow.workflow_id_n8n,
      workflow_name: workflow.workflow_name,
      webhook_url: workflow.webhook_url,
      display_name: workflow.display_name,
      webhook_token: workflow.webhook_token || '',
      tags: workflow.tags,
      description: workflow.description || '',
    });
  };

  const handleCreate = () => {
    setEditingId('NEW');
    setFormData({
      workflow_id_n8n: '',
      workflow_name: '',
      webhook_url: '',
      display_name: '',
      webhook_token: '',
      tags: [],
      description: '',
    });
  };

  const handleSave = async () => {
    try {
      // Validation
      if (!formData.workflow_id_n8n.trim()) {
        toast.error('Workflow ID (n8n) is required');
        return;
      }

      if (!formData.workflow_name.trim()) {
        toast.error('Workflow name is required');
        return;
      }

      if (!formData.webhook_url.trim()) {
        toast.error('Webhook URL is required');
        return;
      }

      if (!formData.webhook_url.startsWith('http://') && !formData.webhook_url.startsWith('https://')) {
        toast.error('Webhook URL must start with http:// or https://');
        return;
      }

      if (!formData.display_name.trim()) {
        toast.error('Display name is required');
        return;
      }

      // Create or update
      if (editingId === 'NEW') {
        await apiClient.registerWorkflow(formData);
        toast.success('Workflow registered successfully');
      } else if (editingId) {
        await apiClient.updateWorkflow(editingId, formData);
        toast.success('Workflow updated successfully');
      }

      setEditingId(null);
      fetchWorkflows();
    } catch (error) {
      toast.error('Failed to save workflow');
      console.error(error);
    }
  };

  const handleDelete = async (id: string, displayName: string) => {
    if (!confirm(`Delete workflow "${displayName}"?`)) {
      return;
    }

    try {
      await apiClient.deleteWorkflow(id);
      toast.success('Workflow deleted');
      fetchWorkflows();
    } catch (error) {
      toast.error('Failed to delete workflow');
      console.error(error);
    }
  };

  const handleExecute = async (id: string, displayName: string) => {
    if (!confirm(`Execute workflow "${displayName}"?\n\nThis will trigger the webhook immediately.`)) {
      return;
    }

    try {
      setExecutingId(id);
      const result = await apiClient.executeRegisteredWorkflow(id, {});

      if (result.success) {
        toast.success(`Workflow executed successfully!${result.execution_id ? ` (ID: ${result.execution_id})` : ''}`);
        fetchWorkflows(); // Reload to update execution count
      } else {
        toast.error(`Execution failed: ${result.error || 'Unknown error'}`);
      }
    } catch (error) {
      toast.error(`Failed to execute workflow: ${error instanceof Error ? error.message : 'Unknown error'}`);
      console.error(error);
    } finally {
      setExecutingId(null);
    }
  };

  const handleCancel = () => {
    setEditingId(null);
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleString();
  };

  const getStatusBadge = (status: 'success' | 'failed' | 'pending' | null) => {
    if (!status) return null;

    if (status === 'success') return <Badge className="bg-primary/10 text-primary border-primary/20">{status}</Badge>;
    if (status === 'failed') return <Badge variant="destructive">{status}</Badge>;
    return <Badge className="bg-secondary/10 text-secondary border-secondary/20">{status}</Badge>;
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="text-muted-foreground">Loading workflow registry...</div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Workflow Registry</h1>
          <p className="text-muted-foreground mt-1">
            Manage webhook-based n8n workflows for execution
          </p>
        </div>
        <Button onClick={handleCreate}>
          <Plus className="w-4 h-4 mr-2" />
          Register Workflow
        </Button>
      </div>

      {/* Form for creating/editing */}
      {editingId && (
        <Card className="p-6 mb-6">
          <h2 className="text-xl font-semibold text-foreground mb-4">
            {editingId === 'NEW' ? 'Register New Workflow' : 'Edit Workflow'}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Workflow ID (n8n) */}
            <div className="space-y-2">
              <Label>Workflow ID (n8n) *</Label>
              <Input
                type="text"
                value={formData.workflow_id_n8n}
                onChange={(e) => setFormData({ ...formData, workflow_id_n8n: e.target.value })}
                placeholder="e.g., q2ChTKwBN9Ig0ZmW"
              />
            </div>

            {/* Workflow Name */}
            <div className="space-y-2">
              <Label>Workflow Name (from n8n) *</Label>
              <Input
                type="text"
                value={formData.workflow_name}
                onChange={(e) => setFormData({ ...formData, workflow_name: e.target.value })}
                placeholder="e.g., Customer Support Email"
              />
            </div>

            {/* Display Name */}
            <div className="space-y-2">
              <Label>Display Name *</Label>
              <Input
                type="text"
                value={formData.display_name}
                onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                placeholder="e.g., Support Email Template"
              />
            </div>

            {/* Webhook URL */}
            <div className="space-y-2">
              <Label>Webhook URL *</Label>
              <Input
                type="url"
                value={formData.webhook_url}
                onChange={(e) => setFormData({ ...formData, webhook_url: e.target.value })}
                placeholder="https://n8n.domain.com/webhook/your-path"
              />
            </div>

            {/* Webhook Token */}
            <div className="space-y-2">
              <Label>Webhook Token (optional)</Label>
              <Input
                type="password"
                value={formData.webhook_token}
                onChange={(e) => setFormData({ ...formData, webhook_token: e.target.value })}
                placeholder="Secret token for X-Webhook-Token header"
              />
            </div>

            {/* Tags */}
            <div className="space-y-2">
              <Label>
                Configuration Tag
                <span className="ml-2 text-xs text-muted-foreground">(determines workflow UI)</span>
              </Label>
              <select
                value={formData.tags?.[0] || ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    tags: e.target.value ? [e.target.value] : [],
                  })
                }
                className="w-full h-10 px-3 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="">No special configuration (default)</option>
                {getAvailableWorkflowTags().map((tag) => (
                  <option key={tag.value} value={tag.value}>
                    {tag.label} - {tag.description}
                  </option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground">
                Select a tag to enable special UI features for this workflow
              </p>
            </div>

            {/* Description */}
            <div className="md:col-span-2 space-y-2">
              <Label>Description</Label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring min-h-[80px]"
                rows={3}
                placeholder="Optional description for this workflow"
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <Button variant="ghost" onClick={handleCancel}>
              Cancel
            </Button>
            <Button onClick={handleSave}>
              {editingId === 'NEW' ? 'Register' : 'Save Changes'}
            </Button>
          </div>
        </Card>
      )}

      {/* Workflows Table */}
      <Card className="overflow-hidden">
        <table className="min-w-full divide-y divide-border">
          <thead className="bg-muted/50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Display Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Workflow ID
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Tags
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Triggers
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Last Triggered
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {workflows.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-6 py-4 text-center text-muted-foreground">
                  No workflows registered yet. Click "Register Workflow" to add one.
                </td>
              </tr>
            ) : (
              workflows.map((workflow) => (
                <tr key={workflow.id} className="hover:bg-muted/50">
                  <td className="px-6 py-4">
                    <div className="font-medium text-foreground">{workflow.display_name}</div>
                    {workflow.description && (
                      <div className="text-sm text-muted-foreground">{workflow.description}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-foreground font-mono">
                    {workflow.workflow_id_n8n}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1">
                      {workflow.tags.map((tag, index) => (
                        <Badge key={index} variant="outline" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-foreground">
                    {workflow.trigger_count}
                  </td>
                  <td className="px-6 py-4 text-sm text-muted-foreground">
                    {formatDate(workflow.last_triggered_at)}
                  </td>
                  <td className="px-6 py-4">
                    {getStatusBadge(workflow.last_execution_status)}
                    {workflow.last_error_message && (
                      <div className="text-xs text-destructive mt-1" title={workflow.last_error_message}>
                        {workflow.last_error_message.substring(0, 50)}
                        {workflow.last_error_message.length > 50 ? '...' : ''}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 text-right text-sm font-medium space-x-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleExecute(workflow.id, workflow.display_name)}
                      disabled={executingId === workflow.id}
                      className="text-primary hover:text-primary/80"
                      title="Test Execute"
                    >
                      {executingId === workflow.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(workflow)}
                      className="text-muted-foreground hover:text-foreground"
                      title="Edit"
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(workflow.id, workflow.display_name)}
                      className="text-destructive hover:text-destructive/80"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </Card>

      {/* Info Box */}
      <Card className="mt-6 p-4 border-primary/20 bg-primary/5">
        <h3 className="font-semibold text-foreground mb-2">How to use:</h3>
        <ul className="text-sm text-muted-foreground space-y-1">
          <li>1. Create a workflow in n8n with a <strong className="text-foreground">Webhook Trigger</strong></li>
          <li>2. Copy the <strong className="text-foreground">Production Webhook URL</strong> from n8n (not Test URL!)</li>
          <li>3. Click <strong className="text-foreground">"Register Workflow"</strong> and paste the URL</li>
          <li>4. Select a <strong className="text-foreground">Configuration Tag</strong> to enable special UI features (e.g., CSV upload for "Insert")</li>
          <li>5. Optionally set a <strong className="text-foreground">Webhook Token</strong> for security</li>
          <li>6. Use the <strong className="text-foreground">Play button</strong> to test execution</li>
        </ul>
      </Card>
    </div>
  );
};
